import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ComponentNode } from '../types/template';
import { DataFetcher } from '../utils/DataFetcher';
import { convertStylesToInline } from '../utils/styleUtils';

interface EventDealItem {
    offer_title: string;
    offer_detail: string;
    merchant_name: string;
    merchant_logo: string;
    url: string;
    banner_image?: string;
}

interface EventDealsComponentProps {
    component: ComponentNode;
    context?: {
        companyId?: string;
        domain?: string;
    };
}

const normalizeEventDeals = (response: any): EventDealItem[] => {
    const merchants = response?.data?.merchants ?? [];

    // Flatten all offers from all merchants
    const allOffers: EventDealItem[] = [];

    merchants.forEach((merchant: any) => {
        const offers = merchant.offers ?? [];
        offers.forEach((offer: any) => {
            allOffers.push({
                offer_title: offer.offer_title ?? '',
                offer_detail: offer.offer_detail ?? '',
                merchant_name: merchant.merchant_name ?? '',
                merchant_logo: merchant.merchant_logo ?? '',
                url: offer.url ?? '#',
                banner_image: offer.banner_image ?? '',
            });
        });
    });

    return allOffers;
};

const EventDealsComponent = async ({
    component,
    context,
}: EventDealsComponentProps) => {
    const request = component.apiConfig ?? component.apiEndpoint;
    if (!request) return null;

    const response = await DataFetcher.fetchRawData(request, {
        companyId: context?.companyId,
        domain: context?.domain,
    });

    const deals = normalizeEventDeals(response);
    if (!deals.length) return null;

    const containerStyles = convertStylesToInline(component.styles);
    const headingText = component.heading ?? 'Event Deals';
    const subHeadingText = component.subHeading ?? 'Exclusive offers for this event.';

    return (
        <section style={containerStyles} className="event-deals-section">
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        {headingText && (
                            <h2 className="display-four n17-color f-38 mb-4 mb-md-6">
                                {headingText}
                            </h2>
                        )}
                        {subHeadingText && (
                            <p className="n17-color mb-6">
                                {subHeadingText}
                            </p>
                        )}

                        {/* Offers Grid */}
                        <div className="row cus-row justify-content-center justify-content-md-start g-3">
                            {deals.map((deal: EventDealItem, index: number) => (
                                <div
                                    key={index}
                                    className="col-9 col-md-6 col-lg-4"
                                >
                                    <div className="offer-card n1-bg-color cus-border border b-eighth p-3 p-md-4 rounded-2 d-flex flex-column h-100">
                                        {/* Merchant Logo */}
                                        <div className="offer-header mb-3">
                                            <div className="merchant-logo-wrapper d-flex justify-content-center align-items-center p-2 n2-bg-color rounded-2 mb-2">
                                                {deal.merchant_logo ? (
                                                    <Image
                                                        src={deal.merchant_logo}
                                                        alt={deal.merchant_name}
                                                        width={80}
                                                        height={40}
                                                        style={{
                                                            objectFit: 'contain',
                                                            maxWidth: '100%',
                                                            height: 'auto',
                                                        }}
                                                    />
                                                ) : (
                                                    <span className="n17-color fw-mid">
                                                        {deal.merchant_name}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-center n17-color fw-mid fs-seven mb-0">
                                                {deal.merchant_name}
                                            </p>
                                        </div>

                                        {/* Offer Title */}
                                        <h3 className="fs-six n17-color fw-mid mb-3 flex-grow-1">
                                            {deal.offer_title}
                                        </h3>

                                        {/* Offer Detail */}
                                        {deal.offer_detail && (
                                            <p className="fs-seven f5-color mb-4">
                                                {deal.offer_detail}
                                            </p>
                                        )}

                                        {/* Get Deal Button */}
                                        <Link
                                            href={deal.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-primary rounded-full px-4 py-2 mt-auto d-inline-block"
                                            style={{
                                                backgroundColor: '#FF6B35',
                                                color: 'white',
                                                textDecoration: 'none',
                                                textAlign: 'center',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Get Deal
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EventDealsComponent;
